import { useState, useEffect, useRef, useCallback } from "react";
import { TOP_VIDEO_GALLERY, HERO_FALLBACK_IMAGE } from "@/lib/heroVideoGallery";

/**
 * HeroVideoPlayer - Seamless Cinematic Background Video Player
 * 
 * Architecture: Dual-buffer video system
 * - videoA and videoB alternate
 * - Next video preloads and starts BEFORE current ends
 * - Opacity-based crossfade (no DOM removal)
 * - Zero blank frames guaranteed
 */
export const HeroVideoPlayer = () => {
  // Track which video is currently visible
  const [activePlayer, setActivePlayer] = useState('A');
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Video element refs
  const videoARef = useRef(null);
  const videoBRef = useRef(null);
  
  // Track video indices
  const currentIndexRef = useRef(0);
  const nextIndexRef = useRef(1);
  
  // Track if next video is ready
  const nextVideoReadyRef = useRef(false);
  
  // Track if transition is in progress
  const transitioningRef = useRef(false);
  
  const videos = TOP_VIDEO_GALLERY;
  
  // Get next index with wrap-around
  const getNextIndex = useCallback((index) => {
    return (index + 1) % videos.length;
  }, [videos.length]);
  
  // Get video ref by player name
  const getVideoRef = useCallback((player) => {
    return player === 'A' ? videoARef : videoBRef;
  }, []);
  
  // Preload video into inactive player
  const preloadNextVideo = useCallback((inactivePlayer) => {
    const videoRef = getVideoRef(inactivePlayer);
    const video = videoRef.current;
    
    if (!video || videos.length <= 1) return;
    
    const nextUrl = videos[nextIndexRef.current]?.url;
    if (!nextUrl) return;
    
    // Only load if URL is different
    if (video.src !== nextUrl) {
      nextVideoReadyRef.current = false;
      video.src = nextUrl;
      video.load();
    }
  }, [getVideoRef, videos]);
  
  // Handle next video loaded and ready
  const handleNextVideoCanPlay = useCallback((player) => {
    if (player !== activePlayer) {
      nextVideoReadyRef.current = true;
    }
  }, [activePlayer]);
  
  // Execute the seamless transition
  const executeTransition = useCallback(() => {
    if (transitioningRef.current) return;
    if (!nextVideoReadyRef.current && videos.length > 1) return;
    
    transitioningRef.current = true;
    
    const newActivePlayer = activePlayer === 'A' ? 'B' : 'A';
    const newActiveRef = getVideoRef(newActivePlayer);
    const oldActiveRef = getVideoRef(activePlayer);
    
    // Start playing new video immediately
    if (newActiveRef.current) {
      newActiveRef.current.currentTime = 0;
      newActiveRef.current.play().catch(() => {});
    }
    
    // Swap visibility
    setActivePlayer(newActivePlayer);
    
    // Update indices
    currentIndexRef.current = nextIndexRef.current;
    nextIndexRef.current = getNextIndex(nextIndexRef.current);
    
    // Reset for next transition
    nextVideoReadyRef.current = false;
    
    // Preload next video after short delay
    setTimeout(() => {
      const inactivePlayer = newActivePlayer === 'A' ? 'B' : 'A';
      preloadNextVideo(inactivePlayer);
      transitioningRef.current = false;
    }, 200);
  }, [activePlayer, getVideoRef, getNextIndex, preloadNextVideo, videos.length]);
  
  // Monitor video time for early transition trigger
  const handleTimeUpdate = useCallback((player) => {
    if (player !== activePlayer) return;
    if (transitioningRef.current) return;
    
    const videoRef = getVideoRef(player);
    const video = videoRef.current;
    
    if (!video) return;
    
    const timeRemaining = video.duration - video.currentTime;
    
    // Trigger transition when ~0.2 seconds remain (ensures overlap)
    if (timeRemaining <= 0.2 && timeRemaining > 0 && nextVideoReadyRef.current) {
      executeTransition();
    }
  }, [activePlayer, getVideoRef, executeTransition]);
  
  // Handle video ended (fallback if timeupdate didn't catch it)
  const handleVideoEnded = useCallback((player) => {
    if (player !== activePlayer) return;
    
    if (videos.length <= 1) {
      // Single video - loop it
      const videoRef = getVideoRef(player);
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      }
      return;
    }
    
    // Execute transition if not already done
    if (!transitioningRef.current) {
      executeTransition();
    }
  }, [activePlayer, getVideoRef, executeTransition, videos.length]);
  
  // Initialize first video
  useEffect(() => {
    if (videos.length === 0) return;
    
    const videoA = videoARef.current;
    if (videoA && !isInitialized) {
      videoA.src = videos[0].url;
      videoA.load();
      
      currentIndexRef.current = 0;
      nextIndexRef.current = videos.length > 1 ? 1 : 0;
    }
  }, [videos, isInitialized]);
  
  // Handle initial video ready
  const handleInitialCanPlay = useCallback(async () => {
    if (isInitialized) return;
    
    const videoA = videoARef.current;
    if (videoA) {
      try {
        videoA.muted = true;
        await videoA.play();
        setIsInitialized(true);
        
        // Start preloading next video
        if (videos.length > 1) {
          setTimeout(() => preloadNextVideo('B'), 500);
        }
      } catch (error) {
        console.warn("HeroVideoPlayer: autoplay blocked, waiting for user interaction");
      }
    }
  }, [isInitialized, preloadNextVideo, videos.length]);
  
  // User interaction to enable playback
  useEffect(() => {
    const enablePlayback = async () => {
      const videoA = videoARef.current;
      if (videoA && !isInitialized) {
        try {
          videoA.muted = true;
          await videoA.play();
          setIsInitialized(true);
          
          if (videos.length > 1) {
            setTimeout(() => preloadNextVideo('B'), 500);
          }
        } catch (e) {
          console.warn("HeroVideoPlayer: playback could not start after user interaction", e);
        }
      }
    };
    
    const events = ['click', 'touchstart', 'scroll', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, enablePlayback, { once: true, passive: true });
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, enablePlayback);
      });
    };
  }, [isInitialized, preloadNextVideo, videos.length]);
  
  // No videos - show fallback
  if (videos.length === 0) {
    return (
      <div className="absolute inset-0 bg-black">
        <img 
          src={HERO_FALLBACK_IMAGE} 
          alt="Pruva Jacuzzi"
          className="w-full h-full object-cover"
        />
        <div 
          className="absolute inset-0" 
          style={{ 
            background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.3) 100%)" 
          }} 
        />
      </div>
    );
  }
  
  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      {/* Fallback image - shows until video plays */}
      <img 
        src={HERO_FALLBACK_IMAGE} 
        alt="Pruva Jacuzzi"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          zIndex: 1,
          opacity: isInitialized ? 0 : 1,
          transition: 'opacity 0.5s ease-out',
          pointerEvents: 'none'
        }}
      />
      
      {/* Video A */}
      <video
        ref={videoARef}
        muted
        playsInline
        preload="auto"
        poster={HERO_FALLBACK_IMAGE}
        onCanPlay={activePlayer === 'A' && !isInitialized ? handleInitialCanPlay : undefined}
        onCanPlayThrough={activePlayer === 'A' && !isInitialized ? handleInitialCanPlay : () => handleNextVideoCanPlay('A')}
        onTimeUpdate={() => handleTimeUpdate('A')}
        onEnded={() => handleVideoEnded('A')}
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          zIndex: activePlayer === 'A' ? 3 : 2,
          opacity: activePlayer === 'A' ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
          pointerEvents: 'none'
        }}
      />
      
      {/* Video B */}
      <video
        ref={videoBRef}
        muted
        playsInline
        preload="metadata"
        poster={HERO_FALLBACK_IMAGE}
        onCanPlayThrough={() => handleNextVideoCanPlay('B')}
        onTimeUpdate={() => handleTimeUpdate('B')}
        onEnded={() => handleVideoEnded('B')}
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          zIndex: activePlayer === 'B' ? 3 : 2,
          opacity: activePlayer === 'B' ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
          pointerEvents: 'none'
        }}
      />
      
      {/* Gradient overlay for text readability - always on top */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          zIndex: 10,
          background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.3) 100%)" 
        }} 
      />
    </div>
  );
};

export default HeroVideoPlayer;
